import React, {useState, useMemo, useEffect} from 'react';
import {Container, Grid, Stack, Title, Text, Group, Button, Badge, List, ThemeIcon, Table, Select, Timeline, Collapse, Chip, Switch} from '@mantine/core';
import {
  IconStar,
  IconUserPlus,
  IconBell,
  IconFolder,
  IconFolderShare,
  IconActivity,
  IconChevronRight,
  IconFile,
  IconMusic,
  IconClock,
  IconCalendar,
  IconUser,
  IconMessage,
  IconFilter,
} from '@tabler/icons-react';
import {useNavigate} from 'react-router-dom';
import {isAudioFile} from '../../utils/file_utils';
import {PlayIcon} from '../../modules/audio_player/audio_player_module';
import {navigateToPathForReason} from '../../modules/navigation/navigation_module';
import {DashboardCard} from './components/DashboardCard';
import {ProjectTable} from './components/ProjectTable';
import {useDashboardData} from './hooks/useDashboardData';
import {ProjectWithStats, CollabRequest, UnreadUpdate, FileData} from './types/dashboard.types';
import {formatTimeAgo, formatDuration} from './utils/timeFormatting';
import {useIsMobile} from '@/utils/user_agent';
import {useSelectorWithRefresh} from '../../store/kysely_module';
import {useCurrentUser} from '../../modules/music_sniper/hooks/useCurrentUser';
import {LoadingIndicator} from '../common/LoadingIndicator';

const WORKSPACE_PREFERENCE_KEY = 'dashboard_workspace_filter';

export const NewDashboard = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const currentUser = useCurrentUser();

  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string | null>(() => {
    const saved = localStorage.getItem(WORKSPACE_PREFERENCE_KEY);
    return saved || null;
  });

  const [workspaces] = useSelectorWithRefresh('getAllWorkspaces', {}, [], { workspace_id: null });

  useEffect(() => {
    if (selectedWorkspaceId) {
      localStorage.setItem(WORKSPACE_PREFERENCE_KEY, selectedWorkspaceId);
    } else {
      localStorage.removeItem(WORKSPACE_PREFERENCE_KEY);
    }
  }, [selectedWorkspaceId]);

  const {
    recentActivity,
    starredData,
    myProjects,
    sharedProjects,
    starredProjects,
    starredFiles,
    collaborationRequests,
    unreadNotifications,
    isLoading,
    refresh
  } = useDashboardData(selectedWorkspaceId || undefined);

  const [myProjectsExpanded, setMyProjectsExpanded] = useState(false);
  const [sharedProjectsExpanded, setSharedProjectsExpanded] = useState(false);
  const [expandedWeeks, setExpandedWeeks] = useState<Set<string>>(new Set());
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [showComments, setShowComments] = useState(true);
  const [activeQuickFilter, setActiveQuickFilter] = useState<'justMe' | 'everyoneButMe' | null>(null);
  const [showFilters, setShowFilters] = useState(false);

  const visibleMyProjects = useMemo(() => {
    const sorted = [...myProjects].sort((a, b) =>
      new Date(b.updated_at || b.created_at).getTime() -
      new Date(a.updated_at || a.created_at).getTime()
    );
    return myProjectsExpanded ? sorted : sorted.slice(0, 5);
  }, [myProjects, myProjectsExpanded]);

  const visibleSharedProjects = useMemo(() => {
    const sorted = [...sharedProjects].sort((a, b) =>
      new Date(b.updated_at || b.created_at).getTime() -
      new Date(a.updated_at || a.created_at).getTime()
    );
    return sharedProjectsExpanded ? sorted : sorted.slice(0, 5);
  }, [sharedProjects, sharedProjectsExpanded]);

  const handleProjectClick = (project: ProjectWithStats) => {
    navigateToPathForReason(
      navigate,
      'navigate_to_project_from_new_dashboard',
      `/workspaces/${project.workspace_id}/projects/${project.id}`
    );
  };

  const handleFileClick = (file: FileData | {attributes?: {duration?: string}; id: string; name: string; workspace_id: string; created_at: string; updated_at: string;}) => {
    navigateToPathForReason(
      navigate,
      'navigate_to_file_from_new_dashboard',
      `/workspaces/${file.workspace_id}/files/${file.id}`
    );
  };

  const handleViewAllWorkspaces = () => {
    navigateToPathForReason(
      navigate,
      'navigate_to_all_workspaces_from_new_dashboard',
      '/workspaces'
    );
  };

  const activityWeeks = useMemo(() => {
    if (!recentActivity?.projectRevisions || recentActivity?.projectRevisions.length === 0) {
      return [];
    }

    const sortedRevisions = [...recentActivity.projectRevisions]
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

    type ActivityItem = {
      id: string;
      text: string;
      time: string;
      projectId: string;
      projectName: string;
      userId: string;
      userName: string;
      isCreated: boolean;
      numFiles: number;
      activityType: 'revision' | 'comment';
      commentText?: string;
      fileId?: string;
    };

    type PersonActivities = {
      userId: string;
      userName: string;
      activities: ActivityItem[];
      projectCount: number;
      fileCount: number;
      commentCount: number;
    };

    type WeekData = {
      weekKey: string;
      weekLabel: string;
      weekStart: Date;
      isCurrentWeek: boolean;
      people: PersonActivities[];
      workspaceId: string | null;
      workspaceName: string | null;
    };

    const getWeekStart = (date: Date) => {
      const d = new Date(date);
      const day = d.getDay();
      const diff = d.getDate() - day;
      return new Date(d.setDate(diff));
    };

    const getWeekLabel = (weekStart: Date, isCurrentWeek: boolean, workspaceName: string | null) => {
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);

      const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
      const dateRange = `${weekStart.toLocaleDateString('en-US', options)} - ${weekEnd.toLocaleDateString('en-US', options)}`;

      if (isCurrentWeek) {
        return workspaceName ? `This Week: ${workspaceName}` : 'This Week';
      }

      return workspaceName ? `${dateRange}: ${workspaceName}` : dateRange;
    };

    const now = new Date();
    const currentWeekStart = getWeekStart(now);
    currentWeekStart.setHours(0, 0, 0, 0);

    const weekWorkspaceMap = new Map<string, Map<string, PersonActivities>>();

    sortedRevisions.forEach(revision => {
      const revisionDate = new Date(revision.created_at);
      const weekStart = getWeekStart(revisionDate);
      weekStart.setHours(0, 0, 0, 0);
      const weekKey = weekStart.toISOString();

      const project = recentActivity?.projects.find((p) => p.id === revision.project_id);
      const user = recentActivity?.users.find((u) => u.id === revision.created_by);
      const userName = user?.name || 'Unknown';
      const projectName = project?.name || 'Unknown project';
      const workspaceId = project?.workspace_id || null;

      const weekWorkspaceKey = `${weekKey}::${workspaceId || 'unknown'}`;

      const maxLength = 30;
      const truncatedName = projectName.length > maxLength ?
        projectName.substring(0, maxLength) + '...' : projectName;

      const isCreated = revision.revision_num === 1;
      const actionText = isCreated ? 'created' : 'updated';
      const filesText = revision.num_new_files ? ` with ${revision.num_new_files} file${revision.num_new_files !== 1 ? 's' : ''}` : '';

      const activity: ActivityItem = {
        id: `revision-${revision.id}`,
        text: `${actionText} "${truncatedName}"${filesText}`,
        time: revision.created_at,
        projectId: revision.project_id,
        projectName: truncatedName,
        userId: revision.created_by,
        userName,
        isCreated,
        numFiles: revision.num_new_files || 0,
        activityType: 'revision'
      };

      if (!weekWorkspaceMap.has(weekWorkspaceKey)) {
        weekWorkspaceMap.set(weekWorkspaceKey, new Map());
      }

      const peopleInWeekWorkspace = weekWorkspaceMap.get(weekWorkspaceKey)!;

      if (!peopleInWeekWorkspace.has(revision.created_by)) {
        peopleInWeekWorkspace.set(revision.created_by, {
          userId: revision.created_by,
          userName,
          activities: [],
          projectCount: 0,
          fileCount: 0,
          commentCount: 0
        });
      }

      peopleInWeekWorkspace.get(revision.created_by)!.activities.push(activity);
    });

    if (showComments && recentActivity?.comments) {
      const commentAttachments = recentActivity.attachments.filter(
        a => a.child_type === 'Comment'
      );

      commentAttachments.forEach(attachment => {
        const comment = recentActivity.comments.find(c => c.id === attachment.child_id);
        if (!comment) return;

        // Comments can only be on Files (not Projects directly)
        if (attachment.parent_type !== 'File') return;

        const fileId = attachment.parent_id;
        const fileAttachment = recentActivity.attachments.find(
          a => a.child_type === 'File' && a.child_id === fileId && a.parent_type === 'Project'
        );
        if (!fileAttachment) return;

        const projectId = fileAttachment.parent_id;
        const project = recentActivity.projects.find(p => p.id === projectId);
        const projectName = project?.name || 'Unknown project';
        const workspaceId = project?.workspace_id || null;

        const commentDate = new Date(comment.created_at);
        const weekStart = getWeekStart(commentDate);
        weekStart.setHours(0, 0, 0, 0);
        const weekKey = weekStart.toISOString();
        const weekWorkspaceKey = `${weekKey}::${workspaceId || 'unknown'}`;

        const user = recentActivity.users.find(u => u.id === comment.created_by);
        const userName = user?.name || 'Unknown';

        const maxLength = 30;
        const truncatedName = projectName.length > maxLength ?
          projectName.substring(0, maxLength) + '...' : projectName;

        const truncatedComment = comment.text.length > 50 ?
          comment.text.substring(0, 50) + '...' : comment.text;

        const activity: ActivityItem = {
          id: `comment-${comment.id}`,
          text: `commented on "${truncatedName}"`,
          time: comment.created_at,
          projectId,
          projectName: truncatedName,
          userId: comment.created_by,
          userName,
          isCreated: false,
          numFiles: 0,
          activityType: 'comment',
          commentText: truncatedComment,
          fileId
        };

        if (!weekWorkspaceMap.has(weekWorkspaceKey)) {
          weekWorkspaceMap.set(weekWorkspaceKey, new Map());
        }

        const peopleInWeekWorkspace = weekWorkspaceMap.get(weekWorkspaceKey)!;

        if (!peopleInWeekWorkspace.has(comment.created_by)) {
          peopleInWeekWorkspace.set(comment.created_by, {
            userId: comment.created_by,
            userName,
            activities: [],
            projectCount: 0,
            fileCount: 0,
            commentCount: 0
          });
        }

        peopleInWeekWorkspace.get(comment.created_by)!.activities.push(activity);
      });
    }

    const weeks: WeekData[] = Array.from(weekWorkspaceMap.entries()).map(([weekWorkspaceKey, peopleMap]) => {
      const [weekKey, workspaceId] = weekWorkspaceKey.split('::');
      const weekStart = new Date(weekKey);
      const isCurrentWeek = weekStart.getTime() === currentWeekStart.getTime();

      const workspaceName = workspaceId && workspaceId !== 'unknown'
        ? workspaces.find(w => w.id === workspaceId)?.name || null
        : null;

      const people = Array.from(peopleMap.values()).map(person => {
        // De-duplicate revision activities by project, but keep comments separate
        const projectActivityMap = new Map<string, ActivityItem>();
        const commentActivities: ActivityItem[] = [];

        person.activities.forEach(activity => {
          if (activity.activityType === 'comment') {
            // Keep all comments separate (don't de-duplicate)
            commentActivities.push(activity);
          } else {
            // De-duplicate revisions by project
            const existing = projectActivityMap.get(activity.projectId);
            if (existing) {
              if (new Date(activity.time).getTime() > new Date(existing.time).getTime()) {
                existing.time = activity.time;
              }
              existing.numFiles += activity.numFiles;
              const actionText = existing.isCreated ? 'created' : 'updated';
              existing.text = `${actionText} "${existing.projectName}" with ${existing.numFiles} file${existing.numFiles !== 1 ? 's' : ''}`;
            } else {
              projectActivityMap.set(activity.projectId, {...activity});
            }
          }
        });

        // Combine revisions and comments, then sort by time
        person.activities = [...Array.from(projectActivityMap.values()), ...commentActivities].sort((a, b) =>
          new Date(b.time).getTime() - new Date(a.time).getTime()
        );

        const uniqueProjects = new Set(person.activities.map(a => a.projectId));
        person.projectCount = uniqueProjects.size;
        person.fileCount = person.activities.reduce((sum, a) => sum + a.numFiles, 0);
        person.commentCount = commentActivities.length;
        return person;
      }).sort((a, b) => {
        const aLatest = a.activities[0]?.time || '';
        const bLatest = b.activities[0]?.time || '';
        return new Date(bLatest).getTime() - new Date(aLatest).getTime();
      });

      return {
        weekKey: weekWorkspaceKey,
        weekLabel: getWeekLabel(weekStart, isCurrentWeek, workspaceName),
        weekStart,
        isCurrentWeek,
        people,
        workspaceId: workspaceId !== 'unknown' ? workspaceId : null,
        workspaceName
      };
    }).sort((a, b) => {
      // Find the most recent activity in each week-workspace
      const aLatestActivity = Math.max(
        ...a.people.flatMap(p => p.activities.map(act => new Date(act.time).getTime()))
      );
      const bLatestActivity = Math.max(
        ...b.people.flatMap(p => p.activities.map(act => new Date(act.time).getTime()))
      );

      // Sort by most recent activity first
      const activityCompare = bLatestActivity - aLatestActivity;
      if (activityCompare !== 0) return activityCompare;

      // If same activity time, sort by week start date
      const timeCompare = b.weekStart.getTime() - a.weekStart.getTime();
      if (timeCompare !== 0) return timeCompare;

      // Finally, alphabetically by workspace name
      return (a.workspaceName || '').localeCompare(b.workspaceName || '');
    });

    return weeks;
  }, [recentActivity, workspaces, showComments]);

  useEffect(() => {
    // Find all "This Week" sections (multiple workspaces can have current week activity)
    const currentWeekKeys = activityWeeks
      .filter(w => w.isCurrentWeek)
      .map(w => w.weekKey);

    // Only update if there are current weeks and they're not already all expanded
    if (currentWeekKeys.length > 0) {
      const needsUpdate = currentWeekKeys.some(key => !expandedWeeks.has(key));
      if (needsUpdate) {
        setExpandedWeeks(new Set(currentWeekKeys));
      }
    }
  }, [activityWeeks]);

  const allActivityUsers = useMemo(() => {
    const userMap = new Map<string, { userId: string; userName: string }>();
    activityWeeks.forEach(week => {
      week.people.forEach(person => {
        if (!userMap.has(person.userId)) {
          userMap.set(person.userId, { userId: person.userId, userName: person.userName });
        }
      });
    });
    return Array.from(userMap.values()).sort((a, b) => a.userName.localeCompare(b.userName));
  }, [activityWeeks]);

  const filteredActivityWeeks = useMemo(() => {
    if (activeQuickFilter === 'everyoneButMe' && currentUser?.id) {
      return activityWeeks.map(week => ({
        ...week,
        people: week.people.filter(person => person.userId !== currentUser.id)
      })).filter(week => week.people.length > 0);
    }
    if (!selectedUserId) return activityWeeks;
    return activityWeeks.map(week => ({
      ...week,
      people: week.people.filter(person => person.userId === selectedUserId)
    })).filter(week => week.people.length > 0);
  }, [activityWeeks, selectedUserId, activeQuickFilter, currentUser?.id]);

  const handleJustMe = () => {
    if (!currentUser?.id) return;

    if (activeQuickFilter === 'justMe') {
      setSelectedUserId(null);
      setActiveQuickFilter(null);
    } else {
      setSelectedUserId(currentUser.id);
      setActiveQuickFilter('justMe');
    }
  };

  const handleEveryoneButMe = () => {
    if (!currentUser?.id) return;

    if (activeQuickFilter === 'everyoneButMe') {
      setSelectedUserId(null);
      setActiveQuickFilter(null);
    } else {
      setSelectedUserId(null);
      setActiveQuickFilter('everyoneButMe');
    }
  };

  const selectPlaceholder = useMemo(() => {
    if (activeQuickFilter === 'justMe') return 'Just me';
    if (activeQuickFilter === 'everyoneButMe') return 'Everyone but me';
    if (!selectedUserId) return 'Filter by person';
    const user = allActivityUsers.find(u => u.userId === selectedUserId);
    return user?.userName || 'Filter by person';
  }, [selectedUserId, allActivityUsers, activeQuickFilter]);

  const handleSelectChange = (value: string | null) => {
    setSelectedUserId(value);
    setActiveQuickFilter(null);
  };

  const workspaceOptions = [
    { value: '', label: 'All Workspaces' },
    ...workspaces.map(w => ({ value: w.id, label: w.name }))
  ];

  if (isLoading && !recentActivity) {
    return <LoadingIndicator />;
  }

  return (
    <Container size="xl" py={{base: 'xs', sm: 'md'}} px={{base: 'xs', sm: 'md'}}>
      <Stack gap="md">
        {!isMobile && (
          <Group justify="space-between" wrap="nowrap">
            <Title order={2} ta='center'>Dashboard</Title>
            <Group gap="md">
              <Select
                placeholder="Filter by workspace"
                data={workspaceOptions}
                value={selectedWorkspaceId || ''}
                onChange={(value) => setSelectedWorkspaceId(value || null)}
                clearable
                searchable
                style={{ minWidth: 200 }}
              />
              <Button
                variant="subtle"
                size="compact-sm"
                rightSection={<IconChevronRight size={16} />}
                onClick={handleViewAllWorkspaces}
              >
                View All Workspaces
              </Button>
            </Group>
          </Group>
        )}

        <Grid gutter="md">
          {/* Starred Projects/Files */}
          {(starredProjects.length > 0 || starredFiles.length > 0) && (
            <Grid.Col span={12}>
              <DashboardCard
                title="Starred"
                icon={<IconStar size={18} />}
              >
                {starredProjects.length > 0 && (
                  <>
                    <Text size="md" fw={600}>
                      Projects
                    </Text>
                    <ProjectTable
                      projects={starredProjects}
                      onProjectClick={handleProjectClick}
                      projectFiles={recentActivity?.files || []}
                      attachments={recentActivity?.attachments || []}
                      projectRevisions={recentActivity?.projectRevisions || []}
                    />
                  </>
                )}
                {starredFiles.length > 0 && (
                  <Stack gap="xs" mt="md">
                    <Text size="md" fw={600}>
                      Files
                    </Text>
                    <Table striped highlightOnHover withRowBorders>
                      {!isMobile && (
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th style={{width: '80px', padding: '8px'}}></Table.Th>
                            <Table.Th></Table.Th>
                            <Table.Th style={{width: '140px', textAlign: 'right'}}>Created</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                      )}
                      <Table.Tbody>
                        {starredFiles.slice(0, 5).map((file) => {
                          const fileDuration = file.duration;

                          return (
                            <Table.Tr key={file.id} style={{cursor: 'pointer'}} onClick={() => handleFileClick(file)}>
                              <Table.Td style={{width: '80px', padding: isMobile ? '6px' : '8px'}}>
                                <Group gap="xs" wrap="nowrap" align="center">
                                  {isAudioFile(file.name) ? (
                                    <PlayIcon fileInfo={file} size={16} />
                                  ) : (
                                    <IconFile size={16} style={{opacity: 0.7}} />
                                  )}
                                  {fileDuration && (
                                    <Text size="sm" ml="xs">
                                      {formatDuration(parseInt(fileDuration))}
                                    </Text>
                                  )}
                                </Group>
                              </Table.Td>
                              <Table.Td style={{padding: isMobile ? '6px' : '8px'}}>
                                <Text size="sm" style={{whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                                  {file.name}
                                </Text>
                              </Table.Td>
                              <Table.Td style={{textAlign: 'right', padding: isMobile ? '6px' : '8px'}}>
                                <Text size="sm" c="dimmed">
                                  {formatTimeAgo(file.created_at, isMobile)}
                                </Text>
                              </Table.Td>
                            </Table.Tr>
                          );
                        })}
                      </Table.Tbody>
                    </Table>
                  </Stack>
                )}
              </DashboardCard>
            </Grid.Col>
          )}

          {/* Collab Requests - Only show if there are any */}
          {false && collaborationRequests.length > 0 && ( // TEMPORARILY DISABLED
            <Grid.Col span={{base: 12, md: 6}}>
              <DashboardCard
                title={`Collab Requests (${collaborationRequests.length})`}
                icon={<IconUserPlus size={18} />}
              >
                <Stack gap="xs">
                  {collaborationRequests.map((request: CollabRequest) => (
                    <Group key={request.id} justify="space-between">
                      <div>
                        <Text size="sm" fw={500}>{request.projectName}</Text>
                        <Text size="xs" c="dimmed">From {request.from} • {request.message}</Text>
                      </div>
                      <Group gap="xs">
                        <Button size="xs" variant="light">Accept</Button>
                        <Button size="xs" variant="subtle">Decline</Button>
                      </Group>
                    </Group>
                  ))}
                </Stack>
              </DashboardCard>
            </Grid.Col>
          )}

          {/* Unreads - Only show if there are any */}
          {false && unreadNotifications.length > 0 && ( // TEMPORARILY DISABLED
            <Grid.Col span={{base: 12, md: 6}}>
              <DashboardCard
                title={`Unreads (${unreadNotifications.length})`}
                icon={<IconBell size={18} />}
              >
                <Stack gap="xs">
                  {unreadNotifications.slice(0, 5).map((notification: UnreadUpdate) => (
                    <Group key={notification.id} gap="xs">
                      <div style={{flex: 1}}>
                        <Text size="sm">
                          <Text component="span" fw={500}>{notification.from}</Text>{' '}
                          {notification.updateType === 'new_files' && 'uploaded files to'}
                          {notification.updateType === 'mention' && 'mentioned you in'}
                          {notification.updateType === 'comment' && 'commented on'}{' '}
                          <Text component="span" fw={500}>{notification.projectName}</Text>
                        </Text>
                        <Text size="xs" c="dimmed">{notification.preview}</Text>
                      </div>
                    </Group>
                  ))}
                </Stack>
              </DashboardCard>
            </Grid.Col>
          )}

          {/* My Projects */}
          <Grid.Col span={12}>
            <DashboardCard
              title="My Projects"
              icon={<IconFolder size={18} />}
            >
              {myProjects.length === 0 ? (
                <Text size="sm" c="dimmed">No projects yet</Text>
              ) : (
                <ProjectTable
                  projects={visibleMyProjects}
                  onProjectClick={handleProjectClick}
                  showLoadMore={myProjects.length > 5 && !myProjectsExpanded}
                  onLoadMore={() => setMyProjectsExpanded(true)}
                  projectFiles={recentActivity?.files || []}
                  attachments={recentActivity?.attachments || []}
                  projectRevisions={recentActivity?.projectRevisions || []}
                />
              )}
            </DashboardCard>
          </Grid.Col>

          {/* Shared With Me */}
          <Grid.Col span={12}>
            <DashboardCard
              title="Shared With Me"
              icon={<IconFolderShare size={18} />}
            >
              {sharedProjects.length === 0 ? (
                <Text size="sm" c="dimmed">No shared projects</Text>
              ) : (
                <ProjectTable
                  projects={visibleSharedProjects}
                  onProjectClick={handleProjectClick}
                  showLoadMore={sharedProjects.length > 5 && !sharedProjectsExpanded}
                  onLoadMore={() => setSharedProjectsExpanded(true)}
                  projectFiles={recentActivity?.files || []}
                  attachments={recentActivity?.attachments || []}
                  projectRevisions={recentActivity?.projectRevisions || []}
                />
              )}
            </DashboardCard>
          </Grid.Col>

          {/* Recent Project Updates */}
          <Grid.Col span={12}>
            <DashboardCard
              title="Recent Project Updates"
              icon={<IconActivity size={18} />}
            >
              {activityWeeks.length === 0 ? (
                <Text size="sm" c="dimmed">No recent activity</Text>
              ) : (
                <>
                  <Group justify="space-between" mb="sm">
                    <Button
                      size="xs"
                      variant="subtle"
                      leftSection={<IconFilter size={16} />}
                      onClick={() => setShowFilters(!showFilters)}
                    >
                      {showFilters ? 'Hide Filters' : 'Show Filters'}
                    </Button>
                  </Group>

                  <Collapse in={showFilters}>
                    <Stack gap="sm" mb="md">
                      <Group gap="lg" wrap="wrap">
                        <Switch
                          label="Show comments"
                          checked={showComments}
                          onChange={(e) => setShowComments(e.currentTarget.checked)}
                          size="sm"
                        />

                        {allActivityUsers.length > 1 && (
                          <Select
                            placeholder={selectPlaceholder}
                            data={allActivityUsers.map(user => ({
                              value: user.userId,
                              label: user.userName
                            }))}
                            value={activeQuickFilter ? null : selectedUserId}
                            onChange={handleSelectChange}
                            clearable
                            searchable
                            size="sm"
                            style={{ minWidth: 200, maxWidth: 300, flex: 1 }}
                          />
                        )}
                      </Group>

                      {allActivityUsers.length > 1 && currentUser?.id && (
                        <Group gap="xs">
                          <Chip
                            checked={activeQuickFilter === 'justMe'}
                            onChange={handleJustMe}
                            size="sm"
                          >
                            Just me
                          </Chip>
                          <Chip
                            checked={activeQuickFilter === 'everyoneButMe'}
                            onChange={handleEveryoneButMe}
                            size="sm"
                          >
                            Everyone but me
                          </Chip>
                        </Group>
                      )}
                    </Stack>
                  </Collapse>

                  <div style={{ transition: 'min-height 0.3s ease-in-out' }}>
                    <Timeline active={filteredActivityWeeks.length - 1} bulletSize={isMobile ? 18 : 24} lineWidth={isMobile ? 1 : 2} style={{ userSelect: 'none' }}>
                      {filteredActivityWeeks.map((week) => {
                    const isExpanded = expandedWeeks.has(week.weekKey);

                    const toggleWeek = () => {
                      const newExpanded = new Set(expandedWeeks);
                      if (newExpanded.has(week.weekKey)) {
                        newExpanded.delete(week.weekKey);
                      } else {
                        newExpanded.add(week.weekKey);
                      }
                      setExpandedWeeks(newExpanded);
                    };

                    return (
                      <Timeline.Item
                        key={week.weekKey}
                        bullet={<IconClock size={14} />}
                        title={
                          <Group
                            gap="xs"
                            style={{ cursor: 'pointer' }}
                            onClick={toggleWeek}
                          >
                            <Text size={isMobile ? "sm" : "md"} fw={600}>
                              {week.weekLabel}
                            </Text>
                            <Text size="xs" c="dimmed">
                              {isExpanded ? '▼' : '▶'}
                            </Text>
                          </Group>
                        }
                      >
                        <Collapse in={true} transitionDuration={300}>
                          {!isExpanded ? (
                            <Stack gap={isMobile ? 4 : "xs"} mt={isMobile ? 4 : "xs"} ml={isMobile ? "xs" : "md"} style={{ cursor: 'pointer' }} onClick={toggleWeek}>
                              {week.people.map((person) => {
                                const parts = [];
                                parts.push(`${person.projectCount} ${person.projectCount === 1 ? 'project' : 'projects'}`);
                                if (person.fileCount > 0) {
                                  parts.push(`${person.fileCount} ${person.fileCount === 1 ? 'file' : 'files'}`);
                                }
                                if (person.commentCount > 0) {
                                  parts.push(`${person.commentCount} ${person.commentCount === 1 ? 'comment' : 'comments'}`);
                                }
                                return (
                                  <Group key={person.userId} gap="xs" wrap="nowrap">
                                    <IconUser size={14} style={{opacity: 0.5, flexShrink: 0}} />
                                    <Text size="sm" c="dimmed">
                                      {person.userName}: {parts.join(', ')}
                                    </Text>
                                  </Group>
                                );
                              })}
                            </Stack>
                          ) : (
                            <Stack gap="md" mt={isMobile ? "xs" : "md"} ml={isMobile ? 0 : "xs"}>
                              {week.people.map((person) => {
                                const parts = [];
                                parts.push(`${person.projectCount} ${person.projectCount === 1 ? 'project' : 'projects'}`);
                                if (person.fileCount > 0) {
                                  parts.push(`${person.fileCount} ${person.fileCount === 1 ? 'file' : 'files'}`);
                                }
                                if (person.commentCount > 0) {
                                  parts.push(`${person.commentCount} ${person.commentCount === 1 ? 'comment' : 'comments'}`);
                                }

                                return (
                                  <Stack key={person.userId} gap="xs">
                                    <Group gap="xs">
                                      <IconUser size={14} style={{opacity: 0.7}} />
                                      <Text size="sm" fw={500}>
                                        {person.userName}
                                      </Text>
                                      <Text size="xs" c="dimmed">
                                        ({parts.join(', ')})
                                      </Text>
                                    </Group>
                                    <Table
                                    striped
                                    highlightOnHover
                                    withRowBorders
                                    mt="xs"
                                    styles={isMobile ? {
                                      table: {
                                        fontSize: '0.75rem'
                                      }
                                    } : undefined}
                                  >
                                    <Table.Tbody>
                                      {person.activities.map((activity) => {
                                        const timeAgo = formatTimeAgo(activity.time, isMobile);
                                        const project = recentActivity?.projects.find((p) => p.id === activity.projectId);

                                        let bounceFile = null;
                                        if (project?.attributes?.bounce_file) {
                                          const projectFileIds = recentActivity?.attachments?.filter(a =>
                                            a.parent_type === 'Project' &&
                                            a.parent_id === project.id &&
                                            a.child_type === 'File'
                                          ).map(a => a.child_id) || [];

                                          const projectFiles = recentActivity?.files?.filter(f => projectFileIds.includes(f.id)) || [];

                                          bounceFile = projectFiles.find(f =>
                                            f.relative_path === project.attributes.bounce_file
                                          ) || null;
                                        }

                                        const bounceDuration = bounceFile ? bounceFile.attributes?.duration : null;

                                        return (
                                          <Table.Tr
                                            key={activity.id}
                                            style={{cursor: 'pointer'}}
                                            onClick={() => {
                                              if (activity.activityType === 'comment' && activity.fileId && project) {
                                                const file = recentActivity?.files?.find(f => f.id === activity.fileId);
                                                if (file) {
                                                  handleFileClick(file);
                                                }
                                              } else if (project) {
                                                handleProjectClick(project as ProjectWithStats);
                                              }
                                            }}
                                          >
                                            {!isMobile && (
                                              <Table.Td style={{width: '100px', padding: '8px'}}>
                                                <Text size="xs" c="dimmed">{timeAgo}</Text>
                                              </Table.Td>
                                            )}
                                            <Table.Td style={{width: '80px', padding: isMobile ? '6px' : '8px'}}>
                                              <Group gap="xs" wrap="nowrap" align="center">
                                                {activity.activityType === 'comment' ? (
                                                  <IconMessage size={16} style={{opacity: 0.7}} />
                                                ) : bounceFile && isAudioFile(bounceFile.name) ? (
                                                  <PlayIcon fileInfo={bounceFile} size={16} />
                                                ) : (
                                                  <IconMusic size={16} style={{opacity: 0.7}} />
                                                )}
                                                {bounceDuration && activity.activityType !== 'comment' && (
                                                  <Text size="sm" ml="xs">
                                                    {formatDuration(parseInt(bounceDuration))}
                                                  </Text>
                                                )}
                                              </Group>
                                            </Table.Td>
                                            <Table.Td style={{padding: isMobile ? '6px' : '8px'}}>
                                              <Stack gap={4}>
                                                <Text size={isMobile ? "xs" : "sm"}>{activity.text}</Text>
                                                {activity.activityType === 'comment' && activity.commentText && (
                                                  <Text size="xs" c="dimmed" fs="italic">"{activity.commentText}"</Text>
                                                )}
                                              </Stack>
                                            </Table.Td>
                                            {isMobile && (
                                              <Table.Td style={{textAlign: 'right', padding: '6px'}}>
                                                <Text size="xs" c="dimmed">{timeAgo}</Text>
                                              </Table.Td>
                                            )}
                                          </Table.Tr>
                                        );
                                      })}
                                    </Table.Tbody>
                                  </Table>
                                  </Stack>
                                );
                              })}
                            </Stack>
                          )}
                        </Collapse>
                      </Timeline.Item>
                    );
                      })}
                    </Timeline>
                  </div>
                </>
              )}
            </DashboardCard>
          </Grid.Col>
        </Grid>
      </Stack>
    </Container>
  );
};
